'use strict';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CurationEditorHelper } from '../../curation-editor-helper';
import { ExternalSearchService } from '@ispm/shared/services/external-search.service';
import { StringUtils } from '@ispm/shared/utils/String.utils';
import { Subject } from 'rxjs';
import { CurationSettings } from '../../../curation-dashboard/curation-setting';

@Component({
  selector: 'gen-curation-editor-table-container',
  templateUrl: 'curation-editor-table-container.component.html',
  styleUrls: ['curation-editor-table-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class CurationEditorTableContainerComponent implements OnInit {
  private _approvedMode: boolean = false;
  private diffFieldsFlagUpdateSubject = new Subject<string>();
  public flagDiffFieldsOnUpdate: boolean = false;
  @Output() validated: EventEmitter<any> = new EventEmitter();
  @Output() itemChanged: EventEmitter<any> = new EventEmitter();
  @Output() itemChangedAndUpdateRow: EventEmitter<any> = new EventEmitter();
  @Input() title: string;
  @Input() tableData: any = { };
  @Input() readOnly: boolean;
  @Input() formName: string;
  @Input() itemTitle: string;
  @Input() revisionTag: string;
  @Input() submitted: boolean = false;
  @Input() itemOverrides: (item: any) => any;
  @Input() prioritizeAvailable = true;
  @Input() tableUpdatedByCode = false; //table is updated by code (not only by user input)
  //userCreatedTag - symbolize that an item was created by the user in the current form.
  //It is passed by the parent component because there is a need to know in which curation the item was created by the user.
  @Input() userCreatedTag: string = CurationSettings.CREATE_TAG_USER_CREATED;
  @Input() userCreatedTagsSet: string[] = [];
  @Input() cloneAvailable: boolean = false;
  @Input() applyClone: boolean = false;
  @Input() set approvedMode (val: boolean){
    this._approvedMode = val;

    if (this.tableUpdatedByCode){
      //update flag with timer in order to bypass the follwoing problem: when curation item is approved and user clicks on Revise,
      //set formData(data: any) of DynamicFormComponent is called with flagDiffFieldsOnUpdate = true and the previous changed fields are marked.
      setTimeout(() => this.flagDiffFieldsOnUpdate = !val, 50);
    }
  }

  get approvedMode(): boolean{
    return this._approvedMode;
  }
  public prioritizeTrackByField: string = CurationEditorHelper.INTERNAL_KEY_PARAM_NAME;
  public schema = NewCurationItem;

  constructor(private externalSearchService: ExternalSearchService) { }

  ngOnInit() { }

  updateContainedData(dataArray: any[], rowData?: any[]) {
    // dataArray contains all tables information and not just the table that was changed
    dataArray.forEach((table) => { // remove empty values from the object
      for (const prop in table) {
        if (table[prop] === null || table[prop] === undefined || table[prop] === '') {
          delete table[prop];
        }
      }
    });
    this.itemChanged.emit(dataArray);
    this.itemChangedAndUpdateRow.emit({dataArray: dataArray,rowData: rowData});
  }

  // this method is invoked from DynamicFormTableComponent.
  addNewItem(eventData) {
    if (! eventData) { return; }
    const newObj = eventData.item;
    if (this.userCreatedTag) {
      newObj[this.userCreatedTag] = true;
    }
    this.updateContainedData(eventData.dataArray, eventData.rowData);

  }

  //this method is invoked from DynamicFormTableComponent.
  cloneItem(eventData) {
    if (! eventData) { return; }
    let newObj = eventData.item;
    newObj = Object.assign(newObj, new NewCurationItem());
    if (this.userCreatedTag) {
      newObj[this.userCreatedTag] = true;
    }
    this.updateContainedData(eventData.dataArray, eventData.rowData);
  }

  //this method is invoked from DynamicFormTableComponent.
  deleteItem(eventData) {
    if (! eventData) { return; }
    const deletedItem = eventData.item;
    // if item was deleted already from the list, it is required to update the curationData only.
    // if item was NOT deleted (as when deleting an item that was saved before) - mark it as 'deleted' and update the curationData
    if (! this.shouldRemoveItemOnDelete(deletedItem)) {
      deletedItem[CurationSettings.TABLE_TAG_DELETED] = true;
      deletedItem[CurationSettings.TABLE_TAG_DEL_REV_ID] = this.updatedItemFieldsContext(deletedItem);
    }
    this.updateContainedData(eventData.dataArray, eventData.rowData);
  }

  // indicate if to remove an item from the list, when Delete action is activated
  public shouldRemoveItemOnDelete(itemDetails) {
    return itemDetails &&
      itemDetails[CurationEditorHelper.INTERNAL_NEW_ITEM_PARAM_NAME];
  }

  /**
   * return a reference to a function the calculate the fields' context of a given item.
   *
   * Note: since that function is invoked from DynamicFormTableComponent.getItemUpdateContext(), it is required to
   *       bind the function to the CurationEditorItemsContainerComponent's this instance.
   */
  public updatedItemFieldsContextRef = (itemDetails) => {
    return this.updatedItemFieldsContext(itemDetails);
  }

  public updatedItemFieldsContext (itemDetails) {
    return this.approvedMode ? null : this.revisionTag + '__' + (itemDetails ? itemDetails[CurationEditorHelper.INTERNAL_KEY_PARAM_NAME] : undefined);
  }

  public itemValidationEventRef = (valid: boolean, itemDetails) => {
    if (! itemDetails || this.isItemDisabled(itemDetails)) { // do not validate readonly forms
      return;
    }
    this.validated.emit({
      elementKey: itemDetails[CurationEditorHelper.INTERNAL_KEY_PARAM_NAME],
      validationStatus: valid
    });
  }

  public getCurationItemNgClass(itemDetails) {
    const bool: boolean = !this.approvedMode &&
      itemDetails &&
      itemDetails[CurationSettings.TABLE_TAG_DEL_REV_ID] !== undefined &&
      itemDetails[CurationSettings.TABLE_TAG_DEL_REV_ID] === this.updatedItemFieldsContext(itemDetails);
    return { 'diff_marker': bool, 'dynamic-form-item-deleted': this.isItemMarkedDeleted(itemDetails)};
  }

  isReadOnlyMode() {
    return this.readOnly && this.readOnly === true;
  }

  isItemMarkedDeleted (item): boolean {
    return (item &&
      item[CurationSettings.TABLE_TAG_DELETED] &&
      item[CurationSettings.TABLE_TAG_DELETED] === true);
  }

  public isItemDisabled(item): boolean {
    return this.isItemMarkedDeleted(item) || this.isReadOnlyMode();
  }

  public isItemEditMode(item): boolean {
    if (! item) {
      return false;
    }
    const newItem = item[CurationEditorHelper.INTERNAL_NEW_ITEM_PARAM_NAME] &&
      item[CurationEditorHelper.INTERNAL_NEW_ITEM_PARAM_NAME] === true;

    const addNewMode = item[CurationEditorHelper.INTERNAL_KEY_PARAM_NAME] && newItem;
    return !(newItem || addNewMode);
  }

  public isItemDeletable(item): boolean {
    if(this.userCreatedTagsSet.length >0) {
      return this.userCreatedTagsSet.some(tag => {
          const r = item[tag];
          return r;
      });
    }
    // Only items that were created by the user can be deleted
    return this.userCreatedTag ? item[this.userCreatedTag] : true;
  }

  public itemListenToDataChange(item): boolean {
    return true;
  }

  public itemUpdateOnBlur(item): boolean {
    return false;
  }
}

class NewCurationItem {
  constructor() {
    this[CurationEditorHelper.INTERNAL_NEW_ITEM_PARAM_NAME] = true;
    this[CurationEditorHelper.INTERNAL_KEY_PARAM_NAME] = StringUtils.generateGuid();
  }
}
